import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../../common/SafeIcon';

const { FiList, FiCheckSquare, FiAlignLeft, FiHelpCircle, FiAlertCircle } = FiIcons;

const QuestionTypeClassifier = ({ questions }) => {
  const [typeDistribution, setTypeDistribution] = useState({});
  const [detectedTypes, setDetectedTypes] = useState([]);
  const [mismatchedTypes, setMismatchedTypes] = useState([]);

  useEffect(() => {
    if (!questions.length) return;
    
    // Process all questions
    const processedQuestions = questions.map(question => {
      const detectedType = detectQuestionType(question.text);
      const declaredType = question.type || 'Unspecified';
      const isMatched = detectedType === declaredType;
      
      return {
        id: question.id,
        text: question.text.substring(0, 80) + (question.text.length > 80 ? '...' : ''),
        detectedType,
        declaredType,
        isMatched
      };
    });
    
    // Calculate type distribution
    const distribution = {};
    processedQuestions.forEach(q => {
      distribution[q.detectedType] = (distribution[q.detectedType] || 0) + 1;
    });
    setTypeDistribution(distribution);
    
    // Set detected types for display
    setDetectedTypes(processedQuestions.slice(0, 10));
    
    // Find mismatched types
    const mismatched = processedQuestions.filter(q => !q.isMatched && q.declaredType !== 'Unspecified');
    setMismatchedTypes(mismatched.slice(0, 5));
    
  }, [questions]);
  
  // Function to detect question type based on text patterns
  const detectQuestionType = (text) => {
    const lowerText = text.toLowerCase();
    
    // Check for multiple choice patterns
    if (
      /\([a-e]\)/.test(text) || 
      /\b[a-e]\.\s/.test(text) ||
      /choose\s+(?:the\s+)?(?:correct|right|best)\s+(?:option|answer|alternative)/i.test(text) ||
      /select\s+(?:the\s+)?(?:correct|right|best)\s+(?:option|answer|alternative)/i.test(text)
    ) {
      return 'Multiple Choice';
    }
    
    // Check for true/false patterns
    if (
      /\btrue\s+or\s+false\b/i.test(lowerText) ||
      /\bt\s*\/\s*f\b/i.test(lowerText) ||
      /\byes\s+or\s+no\b/i.test(lowerText)
    ) {
      return 'True/False';
    }
    
    // Check for fill in the blank patterns
    if (
      /_{2,}/g.test(text) || 
      /\.\.\./g.test(text) ||
      /\bfill\s+in\b/i.test(lowerText) ||
      /\bcomplete\s+(?:the|this)\s+(?:sentence|statement)\b/i.test(lowerText)
    ) {
      return 'Fill in the Blank';
    }
    
    // Check for matching patterns
    if (
      /\bmatch\b/i.test(lowerText) ||
      /\bcolumn\s+[ab]\b/i.test(lowerText) ||
      /\bcolumn\s+(?:one|two|1|2)\b/i.test(lowerText)
    ) {
      return 'Matching';
    }
    
    // Check for short answer patterns
    if (
      /\bbriefly\s+(?:explain|describe|discuss)\b/i.test(lowerText) ||
      /\bshort\s+(?:answer|response)\b/i.test(lowerText) ||
      /\bin\s+(?:a\s+)?few\s+(?:words|sentences)\b/i.test(lowerText)
    ) {
      return 'Short Answer';
    }
    
    // Check for essay patterns
    if (
      /\bessay\b/i.test(lowerText) ||
      /\bdiscuss\s+in\s+detail\b/i.test(lowerText) ||
      /\bwrite\s+(?:a|an)\s+(?:essay|paragraph|response)\b/i.test(lowerText) ||
      /\belaborate\b/i.test(lowerText)
    ) {
      return 'Essay';
    }
    
    // Check for problem solving patterns
    if (
      /\bsolve\b/i.test(lowerText) ||
      /\bcalculate\b/i.test(lowerText) ||
      /\bfind\s+the\s+(?:value|answer|solution)\b/i.test(lowerText) ||
      /\bdetermine\s+the\b/i.test(lowerText)
    ) {
      return 'Problem Solving';
    }
    
    // Default to open-ended question
    return 'Open-Ended';
  };
  
  // Get color class based on question type
  const getTypeColor = (type) => {
    const colorMap = {
      'Multiple Choice': 'bg-blue-100 text-blue-800',
      'True/False': 'bg-green-100 text-green-800',
      'Fill in the Blank': 'bg-yellow-100 text-yellow-800',
      'Matching': 'bg-purple-100 text-purple-800',
      'Short Answer': 'bg-pink-100 text-pink-800',
      'Essay': 'bg-indigo-100 text-indigo-800',
      'Problem Solving': 'bg-orange-100 text-orange-800',
      'Open-Ended': 'bg-gray-100 text-gray-800',
      'Unspecified': 'bg-gray-100 text-gray-500'
    };
    return colorMap[type] || 'bg-gray-100 text-gray-800';
  };
  
  // Prepare pie chart options
  const getPieChartOptions = () => {
    const types = Object.keys(typeDistribution);
    const data = types.map(type => ({
      name: type,
      value: typeDistribution[type]
    }));
    
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'center',
        type: 'scroll'
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '14',
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: false
          },
          data: data
        }
      ]
    };
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <SafeIcon icon={FiList} className="text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-800">Question Type Classification</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h4 className="font-medium text-gray-800 mb-4">Question Type Distribution</h4>
          <div className="h-72">
            <ReactECharts 
              option={getPieChartOptions()} 
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
        
        <div>
          <h4 className="font-medium text-gray-800 mb-4">Type Detection Reference</h4>
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-lg ${getTypeColor('Multiple Choice')}`}>
                Multiple Choice
              </span>
              <span className="text-sm text-gray-600">Options with A, B, C, D, E</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-lg ${getTypeColor('True/False')}`}>
                True/False
              </span>
              <span className="text-sm text-gray-600">"True or False" questions</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-lg ${getTypeColor('Fill in the Blank')}`}>
                Fill in the Blank
              </span>
              <span className="text-sm text-gray-600">Sentences with ___ or ...</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-lg ${getTypeColor('Matching')}`}>
                Matching
              </span>
              <span className="text-sm text-gray-600">Match items from two columns</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-lg ${getTypeColor('Short Answer')}`}>
                Short Answer
              </span>
              <span className="text-sm text-gray-600">Brief explanations (1-3 sentences)</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-lg ${getTypeColor('Essay')}`}>
                Essay
              </span>
              <span className="text-sm text-gray-600">Extended writing responses</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-lg ${getTypeColor('Problem Solving')}`}>
                Problem Solving
              </span>
              <span className="text-sm text-gray-600">Mathematical/logical problems</span>
            </div>
          </div>
        </div>
      </div>
      
      {detectedTypes.length > 0 && (
        <div className="mt-8">
          <h4 className="font-medium text-gray-800 mb-4">Sample Question Classifications</h4>
          <div className="space-y-4">
            {detectedTypes.slice(0, 5).map((item, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start">
                  <div className={`inline-block px-3 py-1 rounded-lg mr-3 ${getTypeColor(item.detectedType)}`}>
                    {item.detectedType}
                  </div>
                  <div className="flex-grow">
                    <p className="text-sm text-gray-700">{item.text}</p>
                    {item.declaredType !== 'Unspecified' && (
                      <div className="mt-2 flex items-center">
                        <SafeIcon 
                          icon={item.isMatched ? FiCheckSquare : FiHelpCircle} 
                          className={item.isMatched ? "text-green-500 mr-1" : "text-yellow-500 mr-1"} 
                        />
                        <span className="text-xs text-gray-600">
                          {item.isMatched 
                            ? `Matches declared type: ${item.declaredType}` 
                            : `Declared as: ${item.declaredType}`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {mismatchedTypes.length > 0 && (
        <div className="mt-8 bg-yellow-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-800 mb-2 flex items-center">
            <SafeIcon icon={FiAlertCircle} className="text-yellow-500 mr-2" />
            Potential Type Mismatches
          </h4>
          <p className="text-sm text-gray-600 mb-3">
            {mismatchedTypes.length} questions may have incorrect type classifications based on their wording.
          </p>
          <div className="space-y-2 text-sm">
            {mismatchedTypes.map((item, index) => (
              <div key={index} className="p-2 bg-white rounded border border-yellow-100">
                <div className="flex items-center mb-1">
                  <span className={`px-2 py-0.5 rounded mr-2 text-xs ${getTypeColor(item.declaredType)}`}>
                    Declared: {item.declaredType}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs ${getTypeColor(item.detectedType)}`}>
                    Detected: {item.detectedType}
                  </span>
                </div>
                <p className="text-gray-700">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionTypeClassifier;